// src/pages/MyPeptides.tsx — peptide_plans + peptide_doses log.
import { useState } from "react";
import { usePeptides, useLogPeptideDose } from "@/lib/queries";
import { Card, PageHeader, Spinner, EmptyState, Button, ErrorBanner } from "@/components/ui";

export function MyPeptides() {
  const { data, isLoading, error } = usePeptides();
  const logDose = useLogPeptideDose();
  const [peptide, setPeptide] = useState("");
  const [doseMg, setDoseMg] = useState("");

  const plans = data?.plans ?? [];
  const doses = data?.doses ?? [];

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!peptide.trim() || !doseMg) return;
    logDose.mutate(
      {
        peptide: peptide.trim(),
        doseMg: Number(doseMg),
        takenAt: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          setPeptide("");
          setDoseMg("");
        },
      }
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="My Peptides" subtitle="Protocols and logged doses." />

      {error ? <ErrorBanner message={(error as Error).message} /> : null}

      <section>
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-ls-muted">Plans</h2>
        {isLoading ? (
          <Spinner />
        ) : plans.length === 0 ? (
          <EmptyState title="No peptide plans yet" />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {plans.map((p) => (
              <Card key={p.id}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-ls-fg">{p.peptide}</p>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-ls-muted">
                    {p.status}
                  </span>
                </div>
                <p className="mt-1 text-xs text-ls-muted">
                  {p.doseMg != null ? `${p.doseMg} mg` : "—"}
                  {p.frequency ? ` · ${p.frequency}` : ""}
                  {p.onWeeks ? ` · ${p.onWeeks}w on / ${p.offWeeks ?? 0}w off` : ""}
                </p>
              </Card>
            ))}
          </div>
        )}
      </section>

      <Card>
        <h2 className="mb-3 text-sm font-medium text-ls-fg">Log a dose</h2>
        {logDose.isError ? (
          <div className="mb-3">
            <ErrorBanner message={(logDose.error as Error).message} />
          </div>
        ) : null}
        <form className="grid grid-cols-1 gap-3 sm:grid-cols-3" onSubmit={submit}>
          <input
            aria-label="peptide"
            placeholder="Peptide"
            value={peptide}
            onChange={(e) => setPeptide(e.target.value)}
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          />
          <input
            aria-label="dose mg"
            type="number"
            step="0.01"
            placeholder="Dose (mg)"
            value={doseMg}
            onChange={(e) => setDoseMg(e.target.value)}
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          />
          <Button type="submit" disabled={logDose.isPending || !peptide.trim() || !doseMg}>
            {logDose.isPending ? "Logging…" : "Log dose"}
          </Button>
        </form>
      </Card>

      <section>
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-ls-muted">
          Recent doses
        </h2>
        {doses.length === 0 ? (
          <EmptyState title="No doses logged yet" />
        ) : (
          <Card>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-ls-muted">
                  <th className="py-1">When</th>
                  <th className="py-1">Peptide</th>
                  <th className="py-1">Dose</th>
                  <th className="py-1">Site</th>
                </tr>
              </thead>
              <tbody>
                {doses.slice(0, 12).map((d) => (
                  <tr key={d.id} className="border-t border-white/5 text-ls-fg">
                    <td className="py-1.5">{new Date(d.takenAt).toLocaleDateString()}</td>
                    <td className="py-1.5">{d.peptide}</td>
                    <td className="py-1.5">{d.doseMg} mg</td>
                    <td className="py-1.5 text-ls-muted">{d.site ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>
    </div>
  );
}
